import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/auth';
import { AuthError } from '../types';
import { Loader, CheckCircle, XCircle } from 'lucide-react';

const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    
    console.log('OAuth Callback - Code:', code);
    console.log('OAuth Callback - Error:', error);
    console.log('VITE_API_URL:', import.meta.env.VITE_API_URL);
    
    if (error) {
      setStatus('error');
      setErrorMessage('OAuth authentication was cancelled or failed');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }
    
    if (!code) {
      setStatus('error');
      setErrorMessage('No authorization code received from Google');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    const handleCallback = async () => {
      try {
        console.log('Calling authService.handleOAuthCallback with code:', code);
        await authService.handleOAuthCallback(code);
        setStatus('success');
        setTimeout(() => navigate('/problems'), 2000);
      } catch (error) {
        console.error('OAuth callback error details:', error);
        setStatus('error');
        setErrorMessage(error instanceof AuthError ? error.message : 'Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      }
    };
    
    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="card text-center">
          {status === 'loading' && (
            <div className="py-8">
              <Loader className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Signing you in with Google...</h2>
              <p className="text-gray-600">Please wait while we complete your authentication.</p>
            </div>
          )}
          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Successfully signed in!</h2>
              <p className="text-gray-600">Redirecting you to your dashboard...</p>
            </div>
          )}
          {status === 'error' && (
            <div className="py-8">
              <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication failed</h2>
              <p className="text-gray-600 mb-4">{errorMessage}</p>
              <p className="text-sm text-gray-500">Redirecting you back to login...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage; 